import styled from 'styled-components';

import { Without } from '@/types/generic';
import { colors } from './MainTheme';

export type ButtonProps = {
  /**
   * A label to show on the button
   */
  label?: string;
  theme?: string;
  icon?: boolean;
  fontWeight?: string | number;
  fontSize?: string;
  width?: string;
  height?: string;
  size?: any;
  children?: any;
  send?: boolean;
  selected?: boolean;
  padding?: string;
  margin?: string;
  img?: boolean;

} & Without<JSX.IntrinsicElements['button'], 'ref'>;

const handleSize = (size, padding = '') => {
  if (padding) return padding;
  switch (size) {
    case 'small':
      return '8px 20px';
    case 'medium':
      return '10px 24px';
    case 'large':
      return '12px 24px';
    default:
      return '8px 20px';
  }
};

const handleImgFilter = (theme, selected) => {
  switch (theme) {
    case 'ai-transparent':
      return 'invert(12%) sepia(100%) saturate(7495%) hue-rotate(264deg) brightness(88%) contrast(127%)';
    default:
      return null;
  }
};

const handleHoverColor = (theme, selected) => {
  switch (theme) {
    case 'ai-transparent':
      return colors.deepPurple;
    default:
      return null;
  }
};

const handleHoverBorder = (theme, selected) => {
  switch (theme) {
    case 'ai-transparent':
      return `1px solid ${colors.deepPurple}`;
    default:
      return null;
  }
};

const handleHoverShadow = (theme, selected) => {
  switch (theme) {
    case 'ai-transparent':
      return 'none';
    default:
      return null;
  }
};

const handleShadow = (theme, selected) => {
  switch (theme) {
    case 'ai-transparent':
      if (selected) return 'none';
      return '0px 2px 1px 0px rgba(0, 0, 0, 0.08)'
    default:
      return null;
  }
};

const handleHoverBackgroundColor = (theme, selected) => {
  switch (theme) {
    case 'ai-transparent':
      return '#E7D1FF';
    case 'ai-secondary':
      return colors.deepPurple20;
    default:
      return null;
  }
};

const handleBackgroundColor = (theme, selected) => {
  switch (theme) {
    case 'primary':
      return colors.green;
    case 'secondary':
      return colors.mint;
    case 'tertiary':
      return colors.darkGreen;
    case 'ai':
      return colors.deepPurple;
    case 'ai-transparent':
      if (selected) return '#E7D1FF';
      return 'white';
    case 'ai-secondary':
      return colors.lightPurple;
    case 'danger':
      return colors.orange;
    case 'cancel':
      return '#E4E4E4';
    default:
      return colors.green;
  }
};

const handleColor = (theme, selected) => {
  switch (theme) {
    case 'primary':
      return colors.white;
    case 'secondary':
      return colors.darkGreen;
    case 'tertiary':
      return colors.white;
    case 'ai':
      return colors.white;
    case 'ai-transparent':
      if (selected) return colors.deepPurple;
      return colors.black;
    case 'ai-secondary':
      return colors.deepPurple;
    case 'danger':
      return colors.green;
    case 'cancel':
      return colors.black;
    default:
      return colors.white;
  }
};

const handleBorder = (theme, selected) => {
  switch (theme) {
    case 'ai-transparent':
      if (selected) return `1px solid ${colors.deepPurple}`;
      return `1px solid ${colors.borderPurple}`;
    default:
      return '0';
  }
};

export const MainButton = styled.button<ButtonProps>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: ${(props) => handleSize(props.$size, props.$padding)};
  margin: ${(props) => (props.$margin ? props.$margin : '0')};
  border-radius: 30px;
  font-family: Montserrat, sans-serif;
  font-weight: ${(props) => (props.$fontWeight ? props.$fontWeight : '700')};
  font-size: ${(props) => (props.$fontSize ? props.$fontSize : '13px')};
  background-color: ${(props) => handleBackgroundColor(props.$theme, props.$selected)};
  color: ${(props) => handleColor(props.$theme, props.$selected)};
  border: ${(props) => handleBorder(props.$theme, props.$selected)};
  width: ${(props) => (props.$width ? props.$width : 'initial')};
  height: ${(props) => (props.$height ? props.$height : 'initial')};
  box-shadow: ${(props) => handleShadow(props.$theme, props.$selected)};
  ${(props) => props.$icon && `
    width: 48px;
    height: 48px;
  `}
  ${(props) => props.$send && `
    &.schedule {
      min-width: 32px;
      min-height: 32px;
      margin-left: -6px;
      margin-bottom: -4px;
      svg {
        display: none;
      }
    }
  `}
  ${(props) => props.$img && `
    img {
      filter: ${handleImgFilter(props.$theme, props.$selected)}
    }
  `}

  cursor: pointer;
  & svg {
    min-width: 24px;
    min-height: 24px;
  }
  &:hover, &:focus {
    border: ${(props) => handleHoverBorder(props.$theme, props.$selected)};
    background-color: ${(props) => handleHoverBackgroundColor(props.$theme, props.$selected)};
    color: ${(props) => handleHoverColor(props.$theme, props.$selected)};
    box-shadow: ${(props) => handleHoverShadow(props.$theme, props.$selected)};
    text-decoration: underline;
    text-underline-offset: 4px;
  }
`;

export const Button = ({
  selected = false, theme, label, fontSize, size, children, send, icon, width, height, padding, margin, img, fontWeight, ...props
}: ButtonProps) => (
  <MainButton
    tabIndex={0}
    type="button"
    $selected={selected}
    $size={size}
    $width={width}
    $height={height}
    $fontSize={fontSize}
    $send={send}
    $theme={theme}
    $icon={icon}
    $padding={padding}
    $margin={margin}
    $fontWeight={fontWeight}
    $img={img}
    {...props}
  >
    {label}
    {children}
  </MainButton>
);
